// Copyright (C) AirWorks Solutions, Inc - All Rights Reserved
// DO NOT REDISTRIBUTE
// UNAUTHORIZED COPYING OF THIS FILE, ANY PART OR WHOLE, VIA ANY MEDIUM IS STRICTLY PROHIBITED
// PROPRIETARY AND CONFIDENTIAL

import React, { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { TextField, InputAdornment } from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { useTheme } from '@mui/system';
import stylesDef from './styles';

// Debounce lives here rather than in react-debounce-input: that library cancels its pending
// notify when the input unmounts, so swapping the grid/map views inside the debounce window
// silently dropped whatever was just typed. This flushes the pending value on unmount instead.
interface ISearchFieldProps {
  value?: string;
  debounceTimeout?: number;
  onChange: (value: string) => void;
  placeholder?: string;
  sx?: object;
  disabled?: boolean;
}

const SearchField = ({ value = '', debounceTimeout = 500, onChange, placeholder = '', sx = undefined, disabled = false }: ISearchFieldProps) => {
  const theme = useTheme();
  const styles = stylesDef(theme);
  const [draft, setDraft] = useState(value);
  const pendingRef = useRef<{ timer: number; value: string } | null>(null);
  const onChangeRef = useRef(onChange);
  onChangeRef.current = onChange;

  // External store updates (reset filters, the other view's field syncing back) take the
  // field over unless the user has an unsent edit pending.
  useEffect(() => {
    if (!pendingRef.current) setDraft(value);
  }, [value]);

  const emitDebounced = (next: string) => {
    setDraft(next);
    if (pendingRef.current) window.clearTimeout(pendingRef.current.timer);
    const timer = window.setTimeout(() => {
      pendingRef.current = null;
      onChangeRef.current(next);
    }, debounceTimeout);
    pendingRef.current = { timer, value: next };
  };

  // Enter and blur fire the pending value immediately, matching how react-debounce-input
  // behaved for every search box before the rewrite.
  const flushPending = () => {
    if (!pendingRef.current) return;
    window.clearTimeout(pendingRef.current.timer);
    const { value: pendingValue } = pendingRef.current;
    pendingRef.current = null;
    onChangeRef.current(pendingValue);
  };

  useEffect(() => () => {
    // Unmount flushes rather than drops - the typed text must survive a view swap.
    if (pendingRef.current) {
      window.clearTimeout(pendingRef.current.timer);
      onChangeRef.current(pendingRef.current.value);
      pendingRef.current = null;
    }
  }, []);

  return (
    <TextField
      value={draft}
      placeholder={placeholder}
      disabled={disabled}
      onChange={(ev) => emitDebounced(ev.target.value)}
      onBlur={flushPending}
      onKeyDown={(ev) => {
        if (ev.key === 'Enter') flushPending();
      }}
      sx={sx ? [styles.textField, sx] : styles.textField}
      InputProps={{
        sx: styles.searchField,
        disableUnderline: true,
        startAdornment: (
          <InputAdornment position="start">
            <SearchIcon />
          </InputAdornment>
        ),
      }}
    />
  );
};

SearchField.propTypes = {
  value: PropTypes.string,
  debounceTimeout: PropTypes.number,
  onChange: PropTypes.func.isRequired,
  placeholder: PropTypes.string,
  sx: PropTypes.shape({}),
  disabled: PropTypes.bool,
};

SearchField.defaultProps = {
  value: '',
  debounceTimeout: 500,
  placeholder: '',
  sx: undefined,
  disabled: false,
};

export default SearchField;
